import { useCallback, useEffect, useState, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

interface UsePdfViewerVirtualizationProps {
  totalRows: number;
  rowHeights: number[];
  parentRef: React.RefObject<HTMLDivElement | null>;
  containerWidth: number | null;
}

interface UsePdfViewerVirtualizationReturn {
  rowVirtualizer: ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>;
  scrollToPage: (pageIndex: number, columnSize: number) => void;
  isScrolling: boolean; // New: Track scrolling state
}

const SCROLL_PAUSE_DELAY = 150; // Pause rendering for 150ms after scroll stops

export const usePdfViewerVirtualization = ({
  totalRows,
  rowHeights,
  parentRef,
  containerWidth,
}: UsePdfViewerVirtualizationProps): UsePdfViewerVirtualizationReturn => {
  // Scroll state management (like bundle page)
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Virtualizer setup
  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => rowHeights[i] || 500,
    overscan: 9, //Decides how many extra items to render outside the viewport
    gap: 1,
  });

  // Handle scroll events to pause rendering
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      // Start scrolling state
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Set timeout to stop scrolling state
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, SCROLL_PAUSE_DELAY);
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [parentRef]);

  // Scroll to specific page function — uses `align: 'start'` so the target row
  // lands at the top of the viewport (just below the actions panel), matching
  // how the up/down arrow keys' onSelectionChange handler scrolls. With `'auto'`
  // the virtualizer would only nudge into view when off-screen, leaving the row
  // wherever it landed (and out of sync with the arrow-key behavior).
  const scrollToPage = useCallback(
    (pageIndex: number, columnSize: number) => {
      if (pageIndex < 0) return;
      const rowIndex = Math.floor(pageIndex / columnSize);
      rowVirtualizer.scrollToIndex(rowIndex, { align: 'start' });
    },
    [rowVirtualizer],
  );

  // Effect to measure virtualizer when container width changes
  useEffect(() => {
    if (containerWidth) {
      rowVirtualizer.measure();
    }
  }, [containerWidth, rowHeights, rowVirtualizer]);

  return {
    rowVirtualizer,
    scrollToPage,
    isScrolling, // Expose scrolling state
  };
};
